import type {
  AIEmployee,
  Category,
  EmployeeProfile, 
  Developer, 
  PricingTier, 
  Capability, 
  Review, 
  DemoVideo, 
  TechnicalSpec 
} from '../types';

/**
 * Mock developers data
 */
export const mockDevelopers: Developer[] = [
  {
    id: 'dev-1',
    name: 'TaqaTech AI Solutions',
    avatar: 'https://via.placeholder.com/64x64/3B82F6/FFFFFF?text=TA',
    website: 'https://taqatech.ai',
    verified: true
  },
  {
    id: 'dev-2',
    name: 'InnovateCorp',
    avatar: 'https://via.placeholder.com/64x64/10B981/FFFFFF?text=IC',
    website: 'https://innovatecorp.com',
    verified: true
  }
];

/**
 * Mock pricing tiers
 */
export const mockPricingTiers: PricingTier[] = [
  {
    id: 'basic',
    name: 'Basic',
    price: 29,
    currency: 'USD',
    period: 'month',
    features: [
      'Up to 1,000 interactions/month',
      'Basic support',
      'Standard response time',
      'Email integration'
    ]
  },
  {
    id: 'pro',
    name: 'Professional',
    price: 99,
    currency: 'USD',
    period: 'month',
    features: [
      'Up to 10,000 interactions/month',
      'Priority support',
      'Fast response time',
      'All integrations',
      'Custom workflows',
      'Analytics dashboard'
    ],
    recommended: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 299,
    currency: 'USD',
    period: 'month',
    features: [
      'Unlimited interactions',
      'Dedicated support',
      'Instant response time',
      'All integrations',
      'Custom AI training',
      'Advanced analytics',
      'White-label options',
      'SLA guarantee'
    ]
  }
];

/**
 * Mock capabilities data
 */
export const mockCapabilities: Capability[] = [
  {
    id: 'multilingual',
    name: 'Multilingual Support',
    description: 'Fluent conversation in 25+ languages with cultural context awareness',
    level: 'expert',
    examples: [
      'English, Spanish, French, German, Chinese, Japanese',
      'Real-time translation',
      'Cultural sensitivity',
      'Localized responses'
    ],
    icon: '🌐'
  },
  {
    id: 'sentiment-analysis',
    name: 'Sentiment Analysis',
    description: 'Advanced emotion detection and response adaptation',
    level: 'advanced',
    examples: [
      'Anger detection and de-escalation',
      'Satisfaction scoring',
      'Emotional context understanding',
      'Mood-appropriate responses'
    ],
    icon: '😊'
  },
  {
    id: 'ticket-management',
    name: 'Ticket Management',
    description: 'Automated support ticket creation, routing, and tracking',
    level: 'expert',
    examples: [
      'Auto-categorization',
      'Priority assessment',
      'Agent routing',
      'Status tracking'
    ],
    icon: '🎫'
  }
];

/**
 * Mock reviews data
 */
export const mockReviews: Review[] = [
  {
    id: 'review-1',
    userId: 'user-1',
    userName: 'Sarah Johnson',
    userAvatar: 'https://via.placeholder.com/48x48/F59E0B/FFFFFF?text=SJ',
    rating: 5,
    comment: 'Amazing AI assistant! Reduced our support response time by 70%. The multilingual capabilities are outstanding.',
    helpful: 23,
    createdAt: new Date('2024-12-10'),
    verified: true
  },
  {
    id: 'review-2',
    userId: 'user-2',
    userName: 'Mike Chen',
    userAvatar: 'https://via.placeholder.com/48x48/8B5CF6/FFFFFF?text=MC',
    rating: 4,
    comment: 'Great performance overall. Setup was easy and integration with our existing systems worked smoothly.',
    helpful: 15,
    createdAt: new Date('2024-12-08'),
    verified: true
  },
  {
    id: 'review-3',
    userId: 'user-3',
    userName: 'Emily Rodriguez',
    userAvatar: 'https://via.placeholder.com/48x48/EF4444/FFFFFF?text=ER',
    rating: 5,
    comment: 'Customer satisfaction scores improved significantly since implementing this AI. Highly recommend!',
    helpful: 31,
    createdAt: new Date('2024-12-05'),
    verified: false
  }
];

/**
 * Mock demo videos
 */
export const mockDemoVideos: DemoVideo[] = [
  {
    id: 'demo-1',
    title: 'Getting Started with Customer Support Pro',
    description: 'Learn how to set up and configure your AI assistant in under 5 minutes',
    url: 'https://via.placeholder.com/640x360/3B82F6/FFFFFF?text=Demo+Video',
    thumbnail: 'https://via.placeholder.com/320x180/3B82F6/FFFFFF?text=Demo+1',
    duration: 180,
    category: 'overview'
  },
  {
    id: 'demo-2',
    title: 'Advanced Multilingual Features',
    description: 'Discover how to leverage multilingual support for global customer service',
    url: 'https://via.placeholder.com/640x360/10B981/FFFFFF?text=Multilingual+Demo',
    thumbnail: 'https://via.placeholder.com/320x180/10B981/FFFFFF?text=Demo+2',
    duration: 240,
    category: 'feature'
  }
];

/**
 * Mock technical specifications
 */
export const mockTechnicalSpecs: TechnicalSpec[] = [
  {
    id: 'response-time',
    name: 'Response Time',
    value: '< 2 seconds',
    category: 'performance'
  },
  {
    id: 'uptime',
    name: 'Uptime SLA',
    value: '99.9%',
    category: 'performance'
  },
  {
    id: 'data-encryption',
    name: 'Data Encryption',
    value: 'AES-256',
    category: 'security'
  },
  {
    id: 'api-integration',
    name: 'API Integration',
    value: 'REST, GraphQL, WebSocket',
    category: 'integration'
  }
];

/**
 * Mock categories for navigation
 */
export const mockCategories: Category[] = [
  { id: '1', name: 'Customer Service', color: 'bg-blue-100 text-blue-800', count: 1 },
  { id: '2', name: 'Content Creation', color: 'bg-green-100 text-green-800', count: 2 },
  { id: '3', name: 'Data Analysis', color: 'bg-purple-100 text-purple-800', count: 1 },
  { id: '4', name: 'Marketing', color: 'bg-pink-100 text-pink-800', count: 4 },
  { id: '5', name: 'Sales', color: 'bg-orange-100 text-orange-800', count: 1 },
  { id: '6', name: 'HR & Recruiting', color: 'bg-indigo-100 text-indigo-800', count: 1 },
  { id: '7', name: 'Finance', color: 'bg-yellow-100 text-yellow-800', count: 2 },
  { id: '8', name: 'IT Support', color: 'bg-gray-100 text-gray-800', count: 1 },
  { id: '9', name: 'Design', color: 'bg-red-100 text-red-800', count: 2 },
  { id: '10', name: 'Education', color: 'bg-teal-100 text-teal-800', count: 1 },
  { id: '11', name: 'Development', color: 'bg-emerald-100 text-emerald-800', count: 2 },
  { id: '12', name: 'Legal', color: 'bg-slate-100 text-slate-800', count: 1 },
  { id: '13', name: 'Productivity', color: 'bg-cyan-100 text-cyan-800', count: 2 },
  { id: '14', name: 'Communication', color: 'bg-violet-100 text-violet-800', count: 2 },
  { id: '15', name: 'Research', color: 'bg-amber-100 text-amber-800', count: 1 },
];

/**
 * Mock AI employees data
 */
export const mockEmployees: AIEmployee[] = [
  {
    id: '1',
    name: 'Customer Support Pro',
    description: 'Advanced AI assistant specializing in customer inquiries, complaint resolution, and support ticket management with multilingual capabilities.',
    icon: '🎧',
    releasedOn: new Date('2024-12-15'),
    category: { name: 'Customer Service', color: 'bg-blue-100 text-blue-800' },
    slug: 'customer-support-pro'
  },
  {
    id: '2',
    name: 'Content Writer AI',
    description: 'Professional content creation assistant for blogs, articles, social media posts, and marketing copy with SEO optimization.',
    icon: '✍️',
    releasedOn: new Date('2024-12-10'),
    category: { name: 'Content Creation', color: 'bg-green-100 text-green-800' },
    slug: 'content-writer-ai'
  },
  {
    id: '3',
    name: 'Data Analyst Expert',
    description: 'Powerful data analysis and visualization assistant capable of processing large datasets and generating actionable insights.',
    icon: '📊',
    releasedOn: new Date('2024-12-08'),
    category: { name: 'Data Analysis', color: 'bg-purple-100 text-purple-800' },
    slug: 'data-analyst-expert'
  },
  {
    id: '4',
    name: 'Sales Assistant Plus',
    description: 'Intelligent sales support AI for lead qualification, follow-ups, and customer relationship management with CRM integration.',
    icon: '💼',
    releasedOn: new Date('2024-12-05'),
    category: { name: 'Sales', color: 'bg-orange-100 text-orange-800' },
    slug: 'sales-assistant-plus'
  },
  {
    id: '5',
    name: 'Marketing Strategist',
    description: 'Comprehensive marketing AI for campaign planning, audience targeting, and performance optimization across multiple channels.',
    icon: '📈',
    releasedOn: new Date('2024-12-01'),
    category: { name: 'Marketing', color: 'bg-pink-100 text-pink-800' },
    slug: 'marketing-strategist'
  },
  {
    id: '6',
    name: 'HR Recruiter Bot',
    description: 'Advanced recruitment assistant for candidate screening, interview scheduling, and talent acquisition workflow automation.',
    icon: '👥',
    releasedOn: new Date('2024-11-28'),
    category: { name: 'HR & Recruiting', color: 'bg-indigo-100 text-indigo-800' },
    slug: 'hr-recruiter-bot'
  },
  {
    id: '7',
    name: 'Finance Manager AI',
    description: 'Financial analysis and reporting assistant with budgeting, expense tracking, and financial forecasting capabilities.',
    icon: '💰',
    releasedOn: new Date('2024-11-25'),
    category: { name: 'Finance', color: 'bg-yellow-100 text-yellow-800' },
    slug: 'finance-manager-ai'
  },
  {
    id: '8',
    name: 'Design Creator Pro',
    description: 'Creative design assistant for graphics, layouts, and visual content creation with brand consistency and style guide adherence.',
    icon: '🎨',
    releasedOn: new Date('2024-11-20'),
    category: { name: 'Design', color: 'bg-red-100 text-red-800' },
    slug: 'design-creator-pro'
  },
  {
    id: '9',
    name: 'IT Support Specialist',
    description: 'Technical support AI for troubleshooting, system administration, and IT infrastructure management with ticket resolution.',
    icon: '🔧',
    releasedOn: new Date('2024-11-15'),
    category: { name: 'IT Support', color: 'bg-gray-100 text-gray-800' },
    slug: 'it-support-specialist'
  },
  {
    id: '10',
    name: 'Learning Tutor AI',
    description: 'Educational assistant for personalized learning experiences, curriculum development, and student progress tracking.',
    icon: '📚',
    releasedOn: new Date('2024-11-10'),
    category: { name: 'Education', color: 'bg-teal-100 text-teal-800' },
    slug: 'learning-tutor-ai'
  },
  {
    id: '11',
    name: 'Social Media Manager',
    description: 'AI-powered social media specialist for content scheduling, engagement tracking, and community management across platforms.',
    icon: '📱',
    releasedOn: new Date('2024-12-18'),
    category: { name: 'Marketing', color: 'bg-pink-100 text-pink-800' },
    slug: 'social-media-manager'
  },
  {
    id: '12',
    name: 'Code Review Assistant',
    description: 'Advanced programming assistant for code analysis, bug detection, and optimization suggestions with multi-language support.',
    icon: '👨‍💻',
    releasedOn: new Date('2024-12-16'),
    category: { name: 'Development', color: 'bg-emerald-100 text-emerald-800' },
    slug: 'code-review-assistant'
  },
  {
    id: '13',
    name: 'Video Editor Pro',
    description: 'Intelligent video editing assistant for automated editing, scene detection, and content optimization for various platforms.',
    icon: '🎬',
    releasedOn: new Date('2024-12-14'),
    category: { name: 'Content Creation', color: 'bg-green-100 text-green-800' },
    slug: 'video-editor-pro'
  },
  {
    id: '14',
    name: 'Legal Advisor AI',
    description: 'Legal research and document analysis assistant for contract review, compliance checking, and legal query resolution.',
    icon: '⚖️',
    releasedOn: new Date('2024-12-12'),
    category: { name: 'Legal', color: 'bg-slate-100 text-slate-800' },
    slug: 'legal-advisor-ai'
  },
  {
    id: '15',
    name: 'Project Manager Bot',
    description: 'Comprehensive project management assistant for task planning, resource allocation, and team coordination with timeline tracking.',
    icon: '📋',
    releasedOn: new Date('2024-12-09'),
    category: { name: 'Productivity', color: 'bg-cyan-100 text-cyan-800' },
    slug: 'project-manager-bot'
  },
  {
    id: '16',
    name: 'Language Translator',
    description: 'Advanced multilingual translation service with context awareness, cultural nuances, and real-time conversation support.',
    icon: '🌍',
    releasedOn: new Date('2024-12-07'),
    category: { name: 'Communication', color: 'bg-violet-100 text-violet-800' },
    slug: 'language-translator'
  },
  {
    id: '17',
    name: 'SEO Optimizer',
    description: 'Search engine optimization specialist for keyword research, content optimization, and performance tracking with analytics.',
    icon: '🔍',
    releasedOn: new Date('2024-12-04'),
    category: { name: 'Marketing', color: 'bg-pink-100 text-pink-800' },
    slug: 'seo-optimizer'
  },
  {
    id: '18',
    name: 'Research Assistant',
    description: 'Academic and business research specialist for data gathering, source verification, and comprehensive report generation.',
    icon: '🔬',
    releasedOn: new Date('2024-12-02'),
    category: { name: 'Research', color: 'bg-amber-100 text-amber-800' },
    slug: 'research-assistant'
  },
  {
    id: '19',
    name: 'Email Marketing Pro',
    description: 'Email campaign specialist for automated sequences, personalization, A/B testing, and conversion optimization.',
    icon: '📧',
    releasedOn: new Date('2024-11-30'),
    category: { name: 'Marketing', color: 'bg-pink-100 text-pink-800' },
    slug: 'email-marketing-pro'
  },
  {
    id: '20',
    name: 'Voice Assistant AI',
    description: 'Natural language processing assistant for voice commands, dictation, and interactive voice response systems.',
    icon: '🎤',
    releasedOn: new Date('2024-11-27'),
    category: { name: 'Communication', color: 'bg-violet-100 text-violet-800' },
    slug: 'voice-assistant-ai'
  },
  {
    id: '21',
    name: 'Photo Editor AI',
    description: 'Intelligent photo editing assistant for automatic enhancement, background removal, and professional retouching.',
    icon: '📸',
    releasedOn: new Date('2024-11-24'),
    category: { name: 'Design', color: 'bg-red-100 text-red-800' },
    slug: 'photo-editor-ai'
  },
  {
    id: '22',
    name: 'Chatbot Builder',
    description: 'Conversational AI development platform for creating intelligent chatbots with natural language understanding.',
    icon: '🤖',
    releasedOn: new Date('2024-11-22'),
    category: { name: 'Development', color: 'bg-emerald-100 text-emerald-800' },
    slug: 'chatbot-builder'
  },
  {
    id: '23',
    name: 'Budget Planner AI',
    description: 'Personal and business financial planning assistant for budget optimization, expense tracking, and savings strategies.',
    icon: '💳',
    releasedOn: new Date('2024-11-19'),
    category: { name: 'Finance', color: 'bg-yellow-100 text-yellow-800' },
    slug: 'budget-planner-ai'
  },
  {
    id: '24',
    name: 'Presentation Maker',
    description: 'Automated presentation creation tool for slide design, content organization, and visual storytelling with templates.',
    icon: '📊',
    releasedOn: new Date('2024-11-17'),
    category: { name: 'Productivity', color: 'bg-cyan-100 text-cyan-800' },
    slug: 'presentation-maker'
  },
];

/**
 * Mock carousel items for announcements and advertisements
 */

/**
 * Mock comprehensive employee profile data
 */
export const mockEmployeeProfile: EmployeeProfile = {
  id: '1',
  name: 'Customer Support Pro',
  tagline: 'Your 24/7 Multilingual Customer Success Partner',
  description: 'Advanced AI assistant specializing in customer inquiries, complaint resolution, and support ticket management with multilingual capabilities. Built with enterprise-grade security and seamless integration capabilities.',
  icon: '🎧',
  avatar: 'https://via.placeholder.com/400x400/3B82F6/FFFFFF?text=Customer+Support+Pro',
  images: [
    'https://via.placeholder.com/800x600/3B82F6/FFFFFF?text=Screenshot+1',
    'https://via.placeholder.com/800x600/1E40AF/FFFFFF?text=Screenshot+2',
    'https://via.placeholder.com/800x600/1D4ED8/FFFFFF?text=Screenshot+3',
    'https://via.placeholder.com/800x600/2563EB/FFFFFF?text=Screenshot+4'
  ],
  releasedOn: new Date('2024-12-15'),
  category: { name: 'Customer Service', color: 'bg-blue-100 text-blue-800' },
  slug: 'customer-support-pro',
  capabilities: mockCapabilities,
  pricing: mockPricingTiers,
  reviews: mockReviews,
  averageRating: 4.7,
  totalReviews: 156,
  version: '2.1.4',
  developer: mockDevelopers[0],
  tags: ['Customer Service', 'AI Assistant', 'Multilingual', 'Enterprise', 'Integration'],
  demoVideos: mockDemoVideos,
  specifications: mockTechnicalSpecs,
  useCases: [
    'E-commerce customer support automation',
    'Multi-channel customer service (email, chat, phone)',
    'International business customer support',
    'High-volume ticket processing',
    'Customer satisfaction improvement',
    'Support team augmentation'
  ],
  requirements: [
    'Minimum 4GB RAM',
    'Internet connection required',
    'Modern web browser (Chrome 90+, Firefox 88+)',
    'API key for integrations'
  ],
  integrations: [
    'Zendesk',
    'Salesforce Service Cloud',
    'Intercom',
    'Slack',
    'Microsoft Teams',
    'WhatsApp Business',
    'Email providers (Gmail, Outlook)',
    'Custom REST APIs'
  ],
  languages: [
    'English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese',
    'Chinese (Simplified)', 'Chinese (Traditional)', 'Japanese', 'Korean',
    'Arabic', 'Russian', 'Hindi', 'Dutch', 'Swedish', 'Norwegian'
  ]
};

/**
 * Get employee profile by slug
 */
export function getEmployeeProfile(slug: string): EmployeeProfile | null {
  // In a real app, this would fetch from an API
  if (slug === 'customer-support-pro') {
    return mockEmployeeProfile;
  }
  
  // For now, return a variation of the main profile for other employees
  const employee = mockEmployees.find(emp => emp.slug === slug);
  if (employee) {
    return {
      ...mockEmployeeProfile,
      ...employee,
      avatar: `https://via.placeholder.com/400x400/3B82F6/FFFFFF?text=${encodeURIComponent(employee.name)}`,
      tagline: `Professional ${employee.category.name} AI Assistant`,
      images: [
        `https://via.placeholder.com/800x600/3B82F6/FFFFFF?text=${encodeURIComponent(employee.name)}+1`,
        `https://via.placeholder.com/800x600/1E40AF/FFFFFF?text=${encodeURIComponent(employee.name)}+2`,
        `https://via.placeholder.com/800x600/1D4ED8/FFFFFF?text=${encodeURIComponent(employee.name)}+3`
      ]
    };
  }
  
  return null;
}

/**
 * Get all employees
 */

/**
 * Get all categories
 */
export function getAllCategories(): Category[] {
  return mockCategories;
}

/**
 * Get employees by category
 */

/**
 * Format release date for display
 */
export function formatReleaseDate(date: Date): string {
  const now = new Date();
  const diffTime = now.getTime() - date.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Released today';
  if (diffDays === 1) return 'Released yesterday';
  if (diffDays < 7) return `Released ${diffDays} days ago`;
  if (diffDays < 30) return `Released ${Math.ceil(diffDays / 7)} weeks ago`;
  
  return `Released on ${date.toLocaleDateString()}`;
}